// Session BDU: the phone keeps the engineer's unfinished SAFed sheet as a local draft (key `safed:record:<jobId>`, unchanged). A draft written
// by an earlier build lacks the fields this build added (the LR1 access answers, the closing remedial work item, the Competent Person who
// witnessed) and its answers may not line up with the printed Annex A form. This adopts such a draft onto the server's record so it still
// loads, keeps what can safely be kept (status, who, when, and any answers whose number matches the printed form), and never reopens a
// stopped test: the server's record wins for that.
import type { SafedRecordView, SafedTestAnswerView, SafedTestView } from "./api";

const pad = <T,>(arr: T[] | undefined, n: number, fill: T): T[] => Array.from({ length: n }, (_, i) => (arr && i < arr.length ? arr[i]! : fill));

export function adoptDraft(draft: SafedRecordView, server: SafedRecordView, tests: readonly SafedTestView[], gateLength: number): SafedRecordView {
  const out: Record<string, SafedTestAnswerView> = {};
  for (const t of tests) {
    const s = server.tests[t.id];
    const d = draft.tests?.[t.id];
    if (!s) {
      if (d) out[t.id] = d;
      continue;
    }
    if (!d) {
      out[t.id] = s;
      continue;
    }
    if (s.status === "stopped") {
      out[t.id] = s;
      continue;
    }
    if (d.annexRev === 2) {
      out[t.id] = d;
      continue;
    }
    const checksFit = Array.isArray(d.checks) && d.checks.length === t.checks.length;
    const valuesFit = Array.isArray(d.values) && d.values.length === t.values.length;
    out[t.id] = {
      ...s,
      status: d.status,
      comment: d.comment ?? s.comment,
      testedBy: d.testedBy ?? s.testedBy,
      testedOn: d.testedOn ?? s.testedOn,
      checks: checksFit ? d.checks : s.checks,
      checkNotes: checksFit && Array.isArray(d.checkNotes) ? pad(d.checkNotes, t.checks.length, "") : s.checkNotes,
      values: valuesFit ? d.values : s.values,
      remedial: s.remedial ?? null,
      recommendation: s.recommendation ?? "",
      annexRev: 2,
    };
  }
  return {
    ...server,
    gate: {
      ...server.gate,
      ...draft.gate,
      ticks: pad(draft.gate?.ticks, gateLength, false),
      accessNeeded: draft.gate?.accessNeeded ?? server.gate.accessNeeded ?? null,
      notNeeded: draft.gate?.notNeeded ?? server.gate.notNeeded ?? {},
      // Whether the job pre-dates the LR1 items is the server's judgement, never the phone's.
      preLr1: server.gate.preLr1 ?? false,
      gateRev: server.gate.gateRev,
    },
    tests: out,
    restoration: draft.restoration ?? server.restoration,
    witnesses: draft.witnesses ?? server.witnesses,
    competentWitness: draft.competentWitness ?? server.competentWitness ?? { name: "", organisation: "", date: "" },
  };
}

/** The same rule the server applies (safedWorkflow.ts gateItemDone), so the phone says what the server will say. */
export function gateItemDone(
  record: SafedRecordView,
  def: { id: string; kind: "tick" | "access" | "lr1"; stressOnly: boolean },
  index: number,
  stress: boolean,
): boolean {
  if (def.stressOnly && !stress) return true;
  const g = record.gate;
  if (def.kind === "access") return g.preLr1 === true || (g.accessNeeded ?? null) !== null;
  if (def.kind === "lr1") {
    if (g.preLr1 === true || g.accessNeeded === "no") return true;
    return g.ticks[index] === true || ((g.notNeeded ?? {})[def.id] ?? "").trim().length > 0;
  }
  return g.ticks[index] === true;
}

/** The unit is not repeated where the printed label already carries it ("Bearing running clearance mm:"). */
export function labelWithUnit(label: string, unit: string | undefined): string {
  if (!unit) return label;
  return label.toLowerCase().includes(` ${unit.toLowerCase()}`) ? label : `${label} (${unit})`;
}
